import React, { useEffect } from 'react';
import { Alert, Box, CircularProgress, Link } from '@mui/material';
import { ChevronLeft as BackIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { GoalPanel } from '../components/planning/GoalPanel';
import { GOALS_RETURN_KEY } from '../components/planning/SavingsGoalsCard';
import { useGoalsSection } from '../components/planning/useGoalsSection';

/**
 * One goal on a page of its own: where a goal opens on phones and narrower
 * screens, with the app's top and bottom bars kept and "‹ Metas" leading
 * back to the goals on the Planning page.
 */
const GoalPage: React.FC = () => {
  const { t } = useTranslation('planning');
  const { goalId } = useParams<{ goalId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromPlanning = Boolean((location.state as { fromPlanning?: boolean } | null)?.fromPlanning);

  const backToGoals = () => {
    try {
      sessionStorage.setItem(GOALS_RETURN_KEY, '1');
    } catch {
      // without storage the Planning page just opens at the top
    }
    if (cameFromPlanning) navigate(-1);
    else navigate('/planning', { replace: true });
  };

  const section = useGoalsSection({ onGoalGone: backToGoals });

  // A new page starts at its top, not where the Planning page was scrolled to.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [goalId]);

  const goal = section.activeGoals.find((candidate) => candidate.id === goalId);

  if (!goal) {
    if (section.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    // Archived, deleted, or a goal of another view than the one in focus.
    return <Navigate to='/planning' replace />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Link
        component='button'
        type='button'
        underline='none'
        onClick={backToGoals}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, alignSelf: 'flex-start', fontSize: 14, fontWeight: 600, minHeight: 32 }}
      >
        <BackIcon fontSize='small' />
        {t('goals.title')}
      </Link>
      {section.error && (
        <Alert severity='error' onClose={section.clearError}>
          {section.error}
        </Alert>
      )}
      <GoalPanel
        goal={goal}
        trail={section.trails[goal.id] ?? []}
        now={section.now}
        showPeople={section.inGroup}
        canContribute={section.canEdit && !goal.isAchieved}
        canAdjust={section.canEdit}
        canModify={section.canModify(goal)}
        canUndo={section.canUndo}
        onContribute={() => section.openContribute(goal)}
        onAdjust={() => section.openAdjust(goal)}
        onMenu={(element) => section.openMenu(element, goal)}
        onUndo={(entry) => section.removeContribution(goal, entry)}
        titleAs='h1'
      />
      {section.dialogs}
    </Box>
  );
};

export default GoalPage;
